<script setup>
import { ref, computed } from 'vue';
import { useI18n } from 'vue-i18n';
import CreationDialog from '@/components/widgets/creationDialog/CreationDialog.vue';
import FormLine from '@/components/widgets/creationDialog/FormLine.vue';
import FormSectionTitle from '@/components/widgets/forms/FormSectionTitle.vue';
import AddressTable from '@/components/Customer/AddressTable.vue';
import ConsigneeForm from '@/components/Customer/ConsigneeForm.vue';
import {
    customerDialogTemplate,
    addressListTemplate
} from '@/config/templates/customerDialog.js';
import { useCustomerUtils } from '@/composables/use-customers';
import { useCustomerAddress } from '@/composables/use-customer-address';
import useCustomForm from '@/composables/use-CustomForm';
import useDialog from '@/composables/use-dialog';
import useAlertStore from '@/stores/alert';
import useLoaderStore from '@/stores/loader';
import BarAction from '@/components/widgets/barActions/BarAction.vue';
import { useUserStore } from '@/stores/user';

defineProps({
    titlePage: {
        type: String,
        default: 'Customer'
    }
});

const emit = defineEmits(['close', 'update', 'saved', 'addressSelected']);
const { t } = useI18n({ useScope: 'global' });
const user = useUserStore();

const addressListFields = addressListTemplate.fields.filter(
    (field) => field.visible !== false
);

const addressListActions = addressListTemplate.actions;

const { show } = useDialog();
const isEditMode = ref(false);
const customer = ref(null);
const loading = ref(false);
const errorMessage = ref(null);
const isPoboxUser = computed(() => user.data?.user?.type === 'POBOX');

const {
    fieldModels,
    clearForm,
    updateFieldValue,
    updatedFromResponse,
    onValidatedSubmit
} = useCustomForm(customerDialogTemplate);

const {
    displayedAddresses,
    setAddresses,
    handleAddressSaved,
    removeAddress,
    validateCustomerForAddress,
    clearAddresses
} = useCustomerAddress();

const sectionVisibilities = ref([]);
const consigneeFormRef = ref(null);

const { getCustomerAddresses, saveCustomer } = useCustomerUtils();

const alertStore = useAlertStore();
const loaderStore = useLoaderStore();

const dialogTitle = computed(() => {
    return isEditMode.value ? 'Edit' : 'Create';
});

const showAddressList = computed(() => {
    return Boolean(customer.value?.id);
});

const mode = computed(() => 'edit');

const initializeSectionVisibilities = () => {
    sectionVisibilities.value = [
        ...customerDialogTemplate.map(() => true),
        true
    ];
};

async function open(customerInfo, parameters = {}) {
    console.debug('parameters:', customerInfo, parameters);
    show.value = true;
    resetForm();

    if (customerInfo) {
        await handleEditMode(customerInfo);
        if (parameters.agency) {
            updateFieldValue('agency', parameters.agency);
        }
    } else {
        handleCreateMode();
    }

    initializeSectionVisibilities();
}

async function handleEditMode(customerInfo) {
    isEditMode.value = true;
    customer.value = customerInfo;

    updatedFromResponse(customerInfo);
    setDefaultAddress(customerInfo.adrdefault);

    await loadAddresses();
}

function handleCreateMode() {
    isEditMode.value = false;
    customer.value = null;
}

function setDefaultAddress(defaultAdr) {
    if (!defaultAdr) return;

    updateFieldValue('docid', defaultAdr.docid);
    updateFieldValue('phone', defaultAdr.phone);
    updateFieldValue('mobile', defaultAdr.mobile);
    updateFieldValue('address', defaultAdr.address);
    updateFieldValue('city', defaultAdr.city);
    updateFieldValue('zip', defaultAdr.zip);
    updateFieldValue('state', defaultAdr.city?.state?.name);
    updateFieldValue('country', defaultAdr.city?.state?.country?.name);
}

function resetForm() {
    clearForm();
    clearAddresses();
    loading.value = false;
    errorMessage.value = null;
}

async function loadAddresses() {
    if (!customer.value) return;

    try {
        loaderStore.showLoader();
        const addressData = await getCustomerAddresses(customer.value.id);
        setAddresses(addressData);
    } catch (error) {
        alertStore.addMessage(error.message);
    } finally {
        loaderStore.hideLoader();
    }
}

async function handleSave() {
    try {
        loading.value = true;
        errorMessage.value = null;

        await onValidatedSubmit(async (values) => {
            if (customer.value?.id) {
                values.id = customer.value.id;
            }

            const result = await saveCustomer(values);
            
            if (result && result.success === false) {
                errorMessage.value = result?.data?.message;
                return;
            }

            if (result) {
                emit('update', result.data);
                emit('saved', result.data);
                close();
            }
        });
    } catch (error) {
        errorMessage.value = error.message;
        alertStore.addMessage(error.message);
    } finally {
        loading.value = false;
    }
}

function handleRetry() {
    errorMessage.value = null;
    handleSave();
}

function handleFieldChanged(modelName, value) {
    updateFieldValue(modelName, value);

    if (modelName === 'city') {
        updateFieldValue('state', value?.state?.name);
        updateFieldValue('country', value?.state?.country?.name);
    }
}

function handleNewAddress() {
    if (!validateCustomerForAddress(customer.value)) return;

    consigneeFormRef.value?.open(null, {
        customerId: customer.value.id,
        customer: customer.value
    });
}

function handleEditAddress(address) {
    if (!validateCustomerForAddress(customer.value)) return;

    consigneeFormRef.value?.open(address.originalData, {
        customerId: customer.value.id,
        customer: customer.value
    });
}

function handleDeleteAddress(address) {
    removeAddress(address.originalData);
}

function handleAddressBarAction(action) {
    if (action === 'new') {
        handleNewAddress();
    } else if (action === 'delete') {
        console.log('Delete action');
    }
}

function getFieldMode(field) {
    if (
        isPoboxUser.value &&
        ['agency', 'agent', 'location', 'groupTariff', 'customerType'].includes(
            field.model
        )
    ) {
        return 'view';
    }
}

function close() {
    show.value = false;
    emit('close');
}

defineExpose({
    open,
    close
});
</script>

<template>
    <CreationDialog
        v-model="show"
        :title="dialogTitle"
        :page="$t(titlePage)"
        :mode="mode"
        :hide-new-action="true"
        :save-button-text="customer ? $t('Save') : $t('Create')"
        @save="handleSave"
        :hideEditAction="true"
        :hideRemoveAction="isPoboxUser ? true : false"
        :saving="loading"
        :error-message="errorMessage"
        @retry="handleRetry"
        @close="close"
    >
        <template v-slot:form>
            <section
                v-for="(section, index) in customerDialogTemplate"
                :key="section.title"
            >
                <FormSectionTitle
                    :title="section.title"
                    v-model="sectionVisibilities[index]"
                />

                <transition name="slide">
                    <v-row v-show="sectionVisibilities[index]" class="mt-3">
                        <FormLine
                            v-for="field in section.fields"
                            :key="field.label"
                            :fullWidth="field.fullWidth"
                            :label="field.label"
                            :mode="getFieldMode(field)"
                            :component="field.component"
                            :error-messages="
                                fieldModels[field.model].errorMessage.value
                            "
                            :oneColumn="field.oneColumn"
                            :model-value="fieldModels[field.model].value.value"
                            :required="field.required"
                            :model-name="field.model"
                            :align="field.align"
                            :return-object="field.returnObject"
                            :item-title="field.itemTitle"
                            :item-value="field.itemValue"
                            :rules="field.rules"
                            :initial-value="field.initialValue"
                            @update:modelValue="
                                (value) =>
                                    handleFieldChanged(field.model, value)
                            "
                            :args="{
                                agency: fieldModels['agency']?.value?.value
                            }"
                        />
                    </v-row>
                </transition>
            </section>

            <section v-if="showAddressList">
                <FormSectionTitle
                    :title="t('Addresses')"
                    v-model="sectionVisibilities[customerDialogTemplate.length]"
                />

                <transition name="slide">
                    <v-card-text
                        v-show="
                            sectionVisibilities[customerDialogTemplate.length]
                        "
                        class="px-0"
                    >
                        <BarAction
                            v-if="Boolean(customer?.id)"
                            :actions="addressListActions"
                            :mode="'view'"
                            class="address-actions"
                            @action="handleAddressBarAction"
                        />

                        <AddressTable
                            :addresses="displayedAddresses"
                            :is-editing="true"
                            :show-actions="false"
                            :fields="addressListFields"
                            @new-address="handleNewAddress"
                            @edit-address="handleEditAddress"
                            @delete-address="handleDeleteAddress"
                        />
                    </v-card-text>
                </transition>
            </section>
        </template>
    </CreationDialog>

    <ConsigneeForm ref="consigneeFormRef" @update="handleAddressSaved" />
</template>

<style scoped>
.address-actions {
    padding: 8px 0;
    border-top: 1px solid #e0e0e0;
    border-bottom: 1px solid #e0e0e0;
    background-color: #f8f9fa;
}

.slide-enter-active,
.slide-leave-active {
    transition: all 0.3s ease;
}

.slide-enter-from,
.slide-leave-to {
    opacity: 0;
    transform: translateY(-10px);
}
</style>
